/**
 * Fee-adjusted top-of-book pricing engine for sports markets.
 *
 * Per-contract fee formula and helpers. Fee *rates* are no longer hardcoded —
 * they come from hydra's per-series fee schedule via `services/hydraFees`.
 * Pure formula functions (perContractFeeCents, applyTakerFee) take an
 * explicit rate so they remain unit-testable; the lookup functions resolve
 * a ticker to its rate and return `null` when the schedule isn't loaded yet.
 */

import type { BookLevel, TeamSide, VenueGameBooks } from '@/types';
import { feeFor } from '@/services/hydraFees';

/**
 * Per-contract taker fee in cents — canonical fee formula (T-006).
 * fee = feeRate * P * (1-P) * 100   where P = priceCents / 100
 *
 * Result is rounded UP to a centicent ($0.0001 = 0.01¢) per Kalshi's fee
 * rounding spec (https://docs.kalshi.com/getting_started/fee_rounding) so
 * pre-trade estimates match Kalshi's per-fill charge before any cross-fill
 * accumulator rebate. The accumulator/rebate logic is settlement-side and
 * shows up in the realized `fee_cost` we'll reconcile against post-fill.
 *
 * `feeRate` is the effective rate (already × series multiplier when sourced
 * from hydra). Polymarket pre-multiplied → caller passes 0.
 */
export function perContractFeeCents(priceCents: number, feeRate: number): number {
  const P = priceCents / 100;
  const rawCents = feeRate * P * (1 - P) * 100;
  // Boundary prices (0/100) and rate=0 give a clean 0; short-circuit before
  // ceiling so the epsilon-shifted Math.ceil doesn't return -0.
  if (rawCents <= 0) return 0;
  // Centicent = 0.01¢ → multiplier 100 against cents. The 1e-9 epsilon
  // absorbs FP fuzz at exactly-aligned values: 0.07 × 0.5 × 0.5 × 100
  // evaluates to 1.7500000000000002 in JS, which would falsely tip ceil to
  // 1.76 without the subtraction. 1e-9 is far above the mantissa noise at
  // these scales and far below the 0.01¢ tick we round to.
  return Math.ceil(rawCents * 100 - 1e-9) / 100;
}

export function bidToAsk(bidCents: number | null | undefined): number | null {
  if (bidCents === null || bidCents === undefined) return null;
  const v = Number(bidCents);
  if (!Number.isFinite(v)) return null;
  return 100 - v;
}

/**
 * Fee computed on a notional `contracts` (default 1000) then spread per contract.
 */
export function applyTakerFee(
  priceCents: number | null,
  feeRate: number,
  contracts = 1000
): number | null {
  if (priceCents === null) return null;
  const p = Number(priceCents);
  if (!Number.isFinite(p) || p <= 0 || p >= 100) return p;

  const feeTotalCents = Math.ceil(perContractFeeCents(p, feeRate) * contracts);
  const feePerContractCents = feeTotalCents / contracts;
  return p + feePerContractCents;
}

/**
 * Look up the per-series taker rate for a Kalshi ticker.
 *
 * Returns:
 *  - `0` for Polymarket (PM- prefix) — Polymarket has no taker fee in our model.
 *  - The effective rate (already × multiplier) for known Kalshi series.
 *  - `null` when fees haven't loaded yet, the series is unknown, or the
 *    series uses `flat` (which doesn't fit the `× P × (1−P)` formula).
 */
export function takerRateForTicker(ticker: string | null | undefined): number | null {
  if (!ticker) return null;
  if (ticker.startsWith('PM-')) return 0;
  const entry = feeFor(ticker);
  if (!entry) return null;
  if (entry.feeType === 'flat') return null;
  return entry.feeTakerRate ?? null;
}

/**
 * Look up the per-series maker rate for a Kalshi ticker.
 *
 * Returns:
 *  - `0` for Polymarket and for Kalshi series with `feeType=quadratic`
 *    (makers don't pay on plain quadratic schedules).
 *  - The effective rate for `quadratic_with_maker_fees` series (~0.0175 × multiplier).
 *  - `null` when fees haven't loaded yet, the series is unknown, or the
 *    series uses `flat`.
 */
export function makerRateForTicker(ticker: string | null | undefined): number | null {
  if (!ticker) return null;
  if (ticker.startsWith('PM-')) return 0;
  const entry = feeFor(ticker);
  if (!entry) return null;
  if (entry.feeType === 'flat') return null;
  return entry.feeMakerRate ?? 0;
}

export function getEffectivePriceFromBid(args: {
  bidCents: number | null;
  isTakerMode: boolean;
  takerFeeRate: number;
}): number | null {
  const { bidCents, isTakerMode, takerFeeRate } = args;
  if (bidCents === null) return null;
  if (!isTakerMode) return bidCents;
  const ask = bidToAsk(bidCents);
  return applyTakerFee(ask, takerFeeRate);
}

export interface TopResult {
  priceCents: number | null;
  liq: number | null;
  rawBidPriceCents: number | null;
}

function bestBid(levels: BookLevel[] | null | undefined): BookLevel | null {
  if (!Array.isArray(levels) || levels.length === 0) return null;
  let best = levels[0];
  for (const lvl of levels) {
    if (lvl && Number(lvl.priceCents) > Number(best.priceCents)) best = lvl;
  }
  return best ?? null;
}

export function topNoFromLevels(args: {
  noLevels: BookLevel[] | null | undefined;
  yesLevels: BookLevel[] | null | undefined;
  isTakerMode: boolean;
  takerFeeRate: number;
}): TopResult {
  const { noLevels, yesLevels, isTakerMode, takerFeeRate } = args;

  if (!Array.isArray(noLevels) || noLevels.length === 0) {
    return { priceCents: null, liq: null, rawBidPriceCents: null };
  }

  if (isTakerMode) {
    if (!Array.isArray(yesLevels) || yesLevels.length === 0) {
      return { priceCents: null, liq: null, rawBidPriceCents: null };
    }
    const bestYes = bestBid(yesLevels);
    if (!bestYes) return { priceCents: null, liq: null, rawBidPriceCents: null };
    const noAsk = bidToAsk(bestYes.priceCents);
    const effective = applyTakerFee(noAsk, takerFeeRate);
    return {
      priceCents: effective,
      liq: bestYes.size ?? null,
      rawBidPriceCents: bestYes.priceCents ?? null,
    };
  }

  const bestNo = bestBid(noLevels);
  if (!bestNo) return { priceCents: null, liq: null, rawBidPriceCents: null };
  const effective = getEffectivePriceFromBid({
    bidCents: bestNo.priceCents ?? null,
    isTakerMode,
    takerFeeRate,
  });
  return {
    priceCents: effective,
    liq: bestNo.size ?? null,
    rawBidPriceCents: bestNo.priceCents ?? null,
  };
}

export interface TeamWinData {
  priceCents: number | null;
  liq: number | null;
  rawBidPriceCents: number | null;
  marketTicker: string | null;
  tokenId?: string;
  noLevels: BookLevel[];
  yesLevels: BookLevel[];
}

/**
 * Resolve top-of-book pricing for a team side with fee adjustment.
 *
 * The taker rate is looked up per-ticker via the fee cache. If the cache
 * isn't ready or the series is unknown, fee adjustment is skipped (rate=0)
 * and the caller can decide whether to flag the row as "fee unknown".
 */
export function getTeamWinData(args: {
  game: VenueGameBooks | null | undefined;
  teamSide: TeamSide;
  isTakerMode: boolean;
}): TeamWinData {
  const { game, teamSide, isTakerMode } = args;
  if (!game) {
    return {
      priceCents: null,
      liq: null,
      rawBidPriceCents: null,
      marketTicker: null,
      noLevels: [],
      yesLevels: [],
    };
  }

  const opp = teamSide === 'away' ? game.markets.home : game.markets.away;
  const noLevels = opp?.no ?? [];
  const yesLevels = opp?.yes ?? [];
  const lookupTicker = opp?.marketTicker ?? null;
  // Fees not loaded / unknown series → rate=0 (no fee adjustment). Treat
  // the displayed price as raw rather than synthesizing a wrong fee.
  const takerFeeRate = takerRateForTicker(lookupTicker) ?? 0;
  const top = topNoFromLevels({
    noLevels,
    yesLevels,
    isTakerMode,
    takerFeeRate,
  });

  return {
    priceCents: top.priceCents,
    liq: top.liq,
    rawBidPriceCents: top.rawBidPriceCents,
    marketTicker: opp?.marketTicker ?? null,
    tokenId: opp?.tokenId,
    noLevels,
    yesLevels,
  };
}

export function next3NoLadders(
  noLevels: BookLevel[],
  bestBidPriceCents: number | null
): BookLevel[] {
  if (!Array.isArray(noLevels) || noLevels.length === 0) return [];
  if (bestBidPriceCents === null) return [];
  const below = noLevels.filter((lvl) => Number(lvl.priceCents) <= bestBidPriceCents);
  below.sort((a, b) => Number(b.priceCents) - Number(a.priceCents));
  return below.slice(0, 3);
}

export function next3YesLadders(
  yesLevels: BookLevel[],
  bestBidPriceCents: number | null
): BookLevel[] {
  if (!Array.isArray(yesLevels) || yesLevels.length === 0) return [];
  if (bestBidPriceCents === null) return [];
  const below = yesLevels.filter((lvl) => Number(lvl.priceCents) <= bestBidPriceCents);
  below.sort((a, b) => Number(b.priceCents) - Number(a.priceCents));
  return below.slice(0, 3);
}

export function liquidityDollars(
  liqContracts: number | null,
  priceCents: number | null
): number | null {
  if (liqContracts === null || priceCents === null) return null;
  const dollars = (Number(liqContracts) * Number(priceCents)) / 100;
  return Number.isFinite(dollars) ? dollars : null;
}

export function computeOrderPriceFromRawBid(
  rawBidPriceCents: number | null,
  isTakerMode: boolean
): number | null {
  if (rawBidPriceCents === null) return null;
  if (!isTakerMode) return rawBidPriceCents;
  return bidToAsk(rawBidPriceCents);
}
